/**
 * Removes practitioner names and business names from address lines
 */
export const removeNameLinesFromAddress = (address: string): string => {
  if (!address) return '';

  const lines = address.split(/[\r\n]+/).map(line => line.trim()).filter(line => line.length > 0);

  const filteredLines = lines.filter(line => {
    // Skip lines that look like practitioner names
    if (/^(Dr\.|Doctor|Mr\.|Mrs\.|Ms\.|Prof\.|Professor)\s+[A-Z][a-z]+/i.test(line)) {
      return false;
    }

    // Skip lines that are just first/last names (2-3 words, all capitalized)
    if (/^[A-Z][a-z]+\s+[A-Z][a-z]+(\s+[A-Z][a-z]+)?$/.test(line)) {
      return false;
    }

    // Skip common business name patterns
    if (/(LLC|Inc|Corp|Corporation|Company|Group|Associates|Partners|Medical|Medicine|Health|Healthcare|Clinic|Center|Centre|Practice|Office|Services)$/i.test(line)) {
      return false;
    }

    // Skip lines that don't contain numbers or common address words
    const hasAddressIndicators = /\d+|street|st|avenue|ave|road|rd|drive|dr|lane|ln|way|blvd|boulevard|highway|hwy|suite|ste|unit|apt|apartment|floor|fl|building|bldg|po box|p\.o\.|zip|city|state|usa|united states/i.test(line);

    if (!hasAddressIndicators) {
      return false;
    }

    return true;
  });

  return filteredLines.join(' ');
};

/**
 * Cleans an address by removing name lines, line breaks and extra whitespace
 */
export const cleanAddressForGoogleMaps = (address: string): string => {
  if (!address) return '';

  // First remove practitioner/business name lines
  const withoutNames = removeNameLinesFromAddress(address);

  // Then clean up formatting
  return withoutNames
    .replace(/[\r\n]+/g, ' ') // Replace any remaining line breaks with spaces
    .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
    .trim();                  // Remove leading/trailing whitespace
};

/**
 * Validates if an address is well-formed for Google Maps search
 */
export const validateAddressForGoogleMaps = (address: string): { isValid: boolean; cleanedAddress: string } => {
  if (!address || address.trim().length === 0) {
    return { isValid: false, cleanedAddress: '' };
  }

  // First, clean the address by removing line breaks
  const cleanedAddress = cleanAddressForGoogleMaps(address);

  // Check for overly long addresses (likely to be malformed)
  if (cleanedAddress.length > 200) {
    return { isValid: false, cleanedAddress };
  }

  // Check for addresses that are just practitioner names/business info without actual address
  const hasStreetNumber = /\d+/.test(cleanedAddress);
  const hasStreetTypes = /(street|st|avenue|ave|road|rd|drive|dr|lane|ln|way|blvd|boulevard|highway|hwy|suite|ste|unit)/i.test(cleanedAddress);

  // Must have at least a number and street type to be a valid address
  if (!hasStreetNumber || !hasStreetTypes) {
    return { isValid: false, cleanedAddress };
  }

  return { isValid: true, cleanedAddress };
};